import type { ColumnDef } from "@tanstack/react-table";
import Table, { TableFooter, TableHeading } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import EmptyState from "@/components/EmptyState";
import { toFormat } from "@/lib/dateHelpers";
import { capitalize } from "lodash";
import type { CcmsAssociateBeneficiaryRecord } from "../../person/queries";

interface CcmsAssociateBeneficiariesTableProps {
  records: CcmsAssociateBeneficiaryRecord[];
  loading?: boolean;
}

type CcmsAssociateBeneficiaryRow = {
  id: string;
  fullName: string | null;
  beneficiaryType: string | null;
  relationship: string | null;
  percent: string | null;
  dateOfBirth: string | null;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
};

const emptyCell = ({ getValue }: { getValue: () => unknown }) => renderEmptyCellState(getValue());

const columns: ColumnDef<CcmsAssociateBeneficiaryRow, unknown>[] = [
  {
    header: "Name",
    id: "fullName",
    accessorKey: "fullName",
    size: 280,
    minSize: 220,
    cell: emptyCell
  },
  {
    header: "Type",
    id: "beneficiaryType",
    accessorKey: "beneficiaryType",
    size: 100,
    minSize: 88,
    cell: emptyCell
  },
  {
    header: "Relationship",
    id: "relationship",
    accessorKey: "relationship",
    cell: emptyCell
  },
  {
    header: "Percent",
    id: "percent",
    accessorKey: "percent",
    size: 72,
    minSize: 64,
    cell: emptyCell
  },
  {
    header: "Date of Birth",
    id: "dateOfBirth",
    accessorKey: "dateOfBirth",
    cell: emptyCell
  },
  {
    header: "Effective Start",
    id: "effectiveStartDate",
    accessorKey: "effectiveStartDate",
    size: 120,
    cell: emptyCell
  },
  {
    header: "Effective End",
    id: "effectiveEndDate",
    accessorKey: "effectiveEndDate",
    size: 120,
    cell: emptyCell
  }
];

export default function CcmsAssociateBeneficiariesTable({
  records,
  loading = false
}: CcmsAssociateBeneficiariesTableProps) {
  const rows: CcmsAssociateBeneficiaryRow[] = records.map((record) => ({
    id: record.id,
    fullName: record.fullName,
    beneficiaryType: record.beneficiaryType ? capitalize(record.beneficiaryType) : null,
    relationship: record.relationship,
    percent: record.percent != null ? `${record.percent}%` : null,
    dateOfBirth: record.dateOfBirth ? toFormat(record.dateOfBirth) : null,
    effectiveStartDate: toFormat(record.effectiveStartDate) ?? null,
    effectiveEndDate: record.effectiveEndDate ? toFormat(record.effectiveEndDate) : null
  }));

  return (
    <>
      <TableHeading
        heading="CCMS Beneficiaries (Associate)"
        body="Read-only legacy beneficiary data from CCMS. This data is not editable in Member 360."
      />
      <Table
        data={rows}
        loading={loading}
        columns={columns}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No CCMS Beneficiaries"
            caption="No associate beneficiary records exist in CCMS for this person."
            iconName="person"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={rows.length} />}
        data-testid="ccms-associate-beneficiaries-table"
      />
    </>
  );
}
